import i18next from "i18next";
import React, { useEffect, useState } from "react";
import { Select, TextInput } from "flowbite-react";
import { Surface } from "../../../../atoms/Surface";
import { AlertMessage } from "../../../../atoms/AlertMessage";
import { Spinner } from "../../../../atoms/Spinner";
import { FieldLabel } from "../../../../atoms/FieldLabel";
import {
  HiInformationCircle,
  HiCreditCard,
  HiCurrencyDollar,
  HiExclamation,
} from "react-icons/hi";
import { Controller, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CustomInput } from "../../../../atoms/CustomInput";
import { RegistrationFormData } from "../types";
import { CharacterCounter } from "../../../../atoms/CharacterCounter";
import { PlanTier, usePermissions } from "../../../../../utils/permissions";
import { Cap, Level } from "../../../../../utils/rbacCatalog";
import useAuth from "../../../../../hooks/useAuth";
import FeatureUpgradePrompt from "../../../../atoms/FeatureUpgradePrompt";
import { PremiumBenefit } from "../../../../atoms/UpgradePromptContent";
import { PlansModal } from "../../../../modals/PlansModal";
import PaymentModuleService, {
  PlanRegistrationResponseDTO,
} from "../../../../../services/paymentModule";

type Props = {
  control: any;
  watch: (name: string) => any;
  errors: any;
  setValue?: UseFormSetValue<RegistrationFormData>;
  readOnly?: boolean;
};

export const PaymentSection: React.FC<Props> = ({
  control,
  watch,
  errors,
  setValue,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const navigate = useNavigate();
  const permissions = usePermissions(user);
  const hasPaymentRequestAccess = permissions.hasFeatureAccess(
    "PAYMENT_REQUEST_CREATION"
  );
  const canReadPaymentConfig = permissions.can(Cap.PAYMENT_CONFIG, Level.VIEW);
  const paymentEnabled = watch("paymentConfig.enabled");
  const paymentType = watch("paymentConfig.paymentType");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [hasActivePayout, setHasActivePayout] = useState<boolean | null>(null);
  const [recurringPlans, setRecurringPlans] = useState<
    PlanRegistrationResponseDTO[]
  >([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  useEffect(() => {
    if (!hasPaymentRequestAccess || !canReadPaymentConfig) return;

    PaymentModuleService.getPayoutAccounts(
      customer_identification,
      user.entity_identification
    )
      .then((response) => {
        const accounts = response.data?.element || [];
        setHasActivePayout(
          accounts.some((account) => account.status === "ACTIVE")
        );
      })
      .catch(() => setHasActivePayout(false));
  }, [
    customer_identification,
    user.entity_identification,
    hasPaymentRequestAccess,
    canReadPaymentConfig,
  ]);

  useEffect(() => {
    if (
      !paymentEnabled ||
      paymentType !== "recurring" ||
      !hasPaymentRequestAccess ||
      !canReadPaymentConfig
    )
      return;
    if (recurringPlans.length > 0) return;

    setLoadingPlans(true);
    PaymentModuleService.getRecurringPlans(
      customer_identification,
      user.entity_identification
    )
      .then((response) => {
        const allPlans = response.data?.element || [];
        setRecurringPlans(allPlans.filter((p) => p.recurrency !== "UNIQUE"));
      })
      .catch(() => setRecurringPlans([]))
      .finally(() => setLoadingPlans(false));
  }, [
    paymentEnabled,
    paymentType,
    hasPaymentRequestAccess,
    canReadPaymentConfig,
    customer_identification,
    user.entity_identification,
  ]);

  const registrationPaymentBenefits: PremiumBenefit[] = [
    {
      id: "collect-at-signup",
      text: t("customRegistration.payment.upgrade.benefit1") as string,
    },
    {
      id: "reduce-no-shows",
      text: t("customRegistration.payment.upgrade.benefit2") as string,
    },
    {
      id: "automate-revenue",
      text: t("customRegistration.payment.upgrade.benefit3") as string,
    },
  ];

  return (
    <Surface>
      <div className="mb-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <HiCreditCard size={20} className="text-green-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-gray-900 text-lg font-semibold">
              {t("customRegistration.payment.title")}
            </h3>
            <p className="text-gray-500 text-sm">
              {t("customRegistration.payment.description")}
            </p>
          </div>
        </div>
      </div>

      {!hasPaymentRequestAccess ? (
        <FeatureUpgradePrompt
          title={t("customRegistration.payment.upgrade.title") as string}
          description={
            t("customRegistration.payment.upgrade.description") as string
          }
          benefitObjects={registrationPaymentBenefits}
          icon={HiCurrencyDollar}
          iconTheme="green"
          upgradeText={t("dashboard.upgrade.key3") as string}
          onUpgrade={() => setShowUpgradeModal(true)}
        />
      ) : (
        <div className="mb-4">
          {canReadPaymentConfig && !hasActivePayout && (
            <AlertMessage className="mb-4" color="warning" icon={HiExclamation}>
              <span className="text-sm font-medium">
                {t("customRegistration.payment.payoutSetupTitle")}
              </span>
              <p className="text-xs mt-1">
                {t("customRegistration.payment.payoutSetupDescription")}{" "}
                <button
                  type="button"
                  onClick={() => navigate("/control-panel/payment-module")}
                  className="text-amber-700 underline font-semibold hover:text-amber-800"
                >
                  {t("customRegistration.payment.payoutSetupLink")}
                </button>{" "}
                {t("customRegistration.payment.payoutSetupSuffix")}
              </p>
            </AlertMessage>
          )}
          <Controller
            name="paymentConfig.enabled"
            control={control}
            render={({ field }) => (
              <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-gray-400 transition-colors">
                <input
                  type="checkbox"
                  checked={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    if (e.target.checked && setValue) {
                      const currentAmount = watch("paymentConfig.amount");
                      if (!currentAmount || currentAmount === 0) {
                        setValue("paymentConfig.amount", 1000);
                      }
                    }
                  }}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="flex-1">
                  <span className="text-gray-900 text-sm font-semibold">
                    {t("customRegistration.payment.enablePayment")}
                  </span>
                  <p className="text-gray-500 text-xs mt-1">
                    {t("customRegistration.payment.enablePaymentDescription")}
                  </p>
                </div>
              </label>
            )}
          />
        </div>
      )}

      {paymentEnabled && (
        <div className="space-y-4 pt-4 border-t border-gray-200 animate-slideIn">
          <div>
            <FieldLabel>
              {t("customRegistration.payment.paymentTypeLabel")}
            </FieldLabel>
            <Controller
              name="paymentConfig.paymentType"
              control={control}
              render={({ field }) => (
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      field.value === "one-time"
                        ? "bg-blue-600 text-white"
                        : `bg-white text-gray-600${
                            readOnly ? "" : " hover:bg-gray-50"
                          }`
                    }${readOnly ? " opacity-60 cursor-not-allowed" : ""}`}
                    onClick={
                      readOnly ? undefined : () => field.onChange("one-time")
                    }
                  >
                    {t("customRegistration.payment.oneTime")}
                  </button>
                  <button
                    type="button"
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      field.value === "recurring"
                        ? "bg-blue-600 text-white"
                        : `bg-white text-gray-600${
                            readOnly ? "" : " hover:bg-gray-50"
                          }`
                    }${readOnly ? " opacity-60 cursor-not-allowed" : ""}`}
                    onClick={
                      readOnly ? undefined : () => field.onChange("recurring")
                    }
                  >
                    {t("customRegistration.payment.recurring")}
                  </button>
                </div>
              )}
            />
          </div>

          <div>
            <CustomInput
              name="paymentConfig.description"
              control={control}
              label={
                t(
                  "customRegistration.payment.paymentDescriptionLabel"
                ) as string
              }
              placeholder={
                t(
                  "customRegistration.payment.paymentDescriptionPlaceholder"
                ) as string
              }
              type="text"
              error={errors.paymentConfig?.description}
              helperText={errors.paymentConfig?.description}
              maxLength={200}
            />
            <CharacterCounter
              currentLength={watch("paymentConfig.description")?.length || 0}
              maxLength={200}
            />
          </div>

          {paymentType === "one-time" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel htmlFor="paymentConfig.amount">
                    {t("customRegistration.payment.paymentAmountLabel")}
                  </FieldLabel>
                  <Controller
                    name="paymentConfig.amount"
                    control={control}
                    render={({ field: amountField }) => (
                      <div>
                        <TextInput
                          id="paymentConfig.amount"
                          type="number"
                          min="1"
                          max="10000"
                          step="0.01"
                          placeholder="10.00"
                          color={
                            errors.paymentConfig?.amount ? "failure" : undefined
                          }
                          className="w-full"
                          value={
                            amountField.value && amountField.value > 0
                              ? (amountField.value / 100).toFixed(2)
                              : ""
                          }
                          onChange={(e) => {
                            const inputValue = e.target.value;

                            if (inputValue === "") {
                              amountField.onChange(0);
                              return;
                            }

                            const value = parseFloat(inputValue);

                            if (!isNaN(value) && value >= 0) {
                              const amountInCents = Math.round(value * 100);
                              amountField.onChange(amountInCents);
                            }
                          }}
                          onBlur={(e) => {
                            const value = parseFloat(e.target.value);

                            if (isNaN(value) || value <= 0) {
                              amountField.onChange(1000);
                            } else if (value > 10000) {
                              amountField.onChange(1000000);
                            } else if (value < 1) {
                              amountField.onChange(100);
                            }
                          }}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {t("paymentRequest.form.amountRange")}
                        </p>
                      </div>
                    )}
                  />
                  {errors.paymentConfig?.amount && (
                    <p className="mt-1 text-xs text-red-600">
                      {errors.paymentConfig.amount.message}
                    </p>
                  )}
                </div>

                <div>
                  <FieldLabel htmlFor="paymentConfig.currency">
                    {t("customRegistration.payment.paymentCurrencyLabel")}
                  </FieldLabel>
                  <Controller
                    name="paymentConfig.currency"
                    control={control}
                    render={({ field: currencyField }) => (
                      <Select
                        {...currencyField}
                        id="paymentConfig.currency"
                        color={
                          errors.paymentConfig?.currency ? "failure" : "gray"
                        }
                      >
                        <option value="USD">USD ($)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="CHF">CHF (Fr.)</option>
                      </Select>
                    )}
                  />
                  {errors.paymentConfig?.currency && (
                    <p className="mt-1 text-xs text-red-600">
                      {errors.paymentConfig.currency.message}
                    </p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <FieldLabel>
                  {t("customRegistration.payment.recurringPlanLabel")}
                </FieldLabel>
                {loadingPlans ? (
                  <div className="flex items-center gap-2 py-2">
                    <Spinner size="sm" />
                    <span className="text-sm text-gray-500">
                      {t("customRegistration.payment.loadingPlans")}
                    </span>
                  </div>
                ) : !canReadPaymentConfig ? (
                  <AlertMessage color="warning" icon={HiInformationCircle}>
                    <span className="text-xs">
                      {t("customRegistration.payment.plansNeedAccess")}
                    </span>
                  </AlertMessage>
                ) : recurringPlans.length > 0 ? (
                  <Controller
                    name="paymentConfig.plan_code"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} id="paymentConfig.plan_code">
                        <option value="">
                          {t(
                            "customRegistration.payment.selectPlanPlaceholder"
                          )}
                        </option>
                        {recurringPlans.map((plan) => (
                          <option key={plan.plan_code} value={plan.plan_code}>
                            {plan.description}
                            {" · "}
                            {new Intl.NumberFormat(i18next.language || "en", {
                              style: "currency",
                              currency: plan.currency,
                              minimumFractionDigits: 2,
                            }).format(plan.amount / 100)}
                            /
                            {plan.recurrency === "MONTHLY"
                              ? t("customRegistration.payment.perMonth")
                              : t("customRegistration.payment.perYear")}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                ) : (
                  <AlertMessage color="warning" icon={HiExclamation}>
                    <span className="text-sm">
                      {t(
                        "customRegistration.payment.noRecurringPlansAvailable"
                      )}{" "}
                      <button
                        type="button"
                        onClick={() =>
                          navigate("/control-panel/payment-module")
                        }
                        className="text-amber-700 underline font-semibold hover:text-amber-800"
                      >
                        {t("customRegistration.payment.createOne")}
                      </button>{" "}
                      {t("customRegistration.payment.inPaymentModuleFirst")}
                    </span>
                  </AlertMessage>
                )}
                {errors.paymentConfig?.plan_code && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.paymentConfig.plan_code.message}
                  </p>
                )}
              </div>
            </>
          )}

          <AlertMessage color="info" icon={HiInformationCircle}>
            <span className="text-xs">
              {t("customRegistration.payment.paymentInfo")}
            </span>
          </AlertMessage>
        </div>
      )}

      <PlansModal
        showPlansModal={showUpgradeModal}
        setShowPlansModal={setShowUpgradeModal}
        targetPlan={PlanTier.ENTERPRISE}
      />
    </Surface>
  );
};
